<template>
  <TasksAccordion
    :value="status"
    :disabled="totalTasks === 0"
    viewport="desktop"
    class="border border-secondary rounded-lg bg-secondary"
    :status="status"
    :count="totalTasks"
    :countRemaining="totalTasks - tasks?.length"
    :loading="isInitiallyLoading"
    :showLoadMore="!isLastPage && !isInitiallyLoading"
    @load-more="getTasks()"
  >
    <template #default>
      <Task
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        type="list"
        :class="{
          'border-b-0':
            (isLastPage && last(tasks) === task) || isInitiallyLoading,
        }"
        @click="emit('click:task', task)"
      />
    </template>
  </TasksAccordion>
</template>

<script setup>
// Vue utilities
import { onMounted, ref, computed } from "vue";
import { storeToRefs } from "pinia";
import route from "ziggy-js";
import axios from "axios";
import { uniqBy, last } from "lodash";

// Import Components
import Task from "@/SharedComponents/Passport/Steps/Tasks/Partials/Task.vue";
import { TasksAccordion } from "@/SharedComponents/Tasks/TasksAccordion";
import { useTaskEvents } from "@/Composables/useTaskEvents";

// Import Components
import { useFilters } from "@/Composables/useFilters";

// Emits
const emit = defineEmits(["click:task", "update:loading"]);

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Define Props
const props = defineProps({
  filter: { type: Object, required: true },
  status: { type: String, required: true },
});

// Refs
const totalTasks = ref(0);
const tasks = ref([]);
const page = ref(1);
const isLastPage = ref(false);
const isLoading = ref(false);
const isInitiallyLoading = ref(true);
const per_page = ref(3);

// Computed props
const filtersWithStatus = computed(() => {
  return {
    order_by: "desc",
    sort_by: "created_at",
    tenant: globalFilterValues.value.tenant,
    tenant_only: false,
    archived_only: false,
    ...props.filter,
    status: [props.status],
  };
});

useTaskEvents({
  tasks,
  filters: filtersWithStatus,
  isLastPage,
  callback: (change) => {
    if (isLoading.value) {
      return;
    }

    totalTasks.value = totalTasks.value + change;
    emit("update:loading", {
      status: props.status,
      fetchingStatus: "loaded",
      count: totalTasks.value,
    });
  },
});

// Functions
async function getTasks() {
  if (isLastPage.value === true || isLoading.value === true) {
    return;
  }

  // Update the isLoading
  isLoading.value = true;

  // Emit update:loading
  emit("update:loading", {
    status: props.status,
    fetchingStatus: "loading",
    count: totalTasks.value,
  });

  const parameters = {
    page: page.value,
    tenant: globalFilterValues.value.tenant,
    tenant_only: false,
    archived_only: false,
    per_page: per_page.value,
    status: [props.status],
    ...props.filter,
  };

  const url = route("api.tasks.search");

  await axios
    .post(url, parameters)
    .then((response) => {
      // Append the newly retrieved tasks to the list of already loaded tasks.
      tasks.value = uniqBy([...tasks.value, ...response.data.data], "id");

      // Assign the total tasks to totalTasks.
      totalTasks.value = response.data.meta.total;

      // Increment the page number to load the next batch of tasks.
      page.value = page.value + 1;

      // There are no more subsequent pages to load.
      if (response.data.links.next === null) {
        isLastPage.value = true;
      }

      // Emit update:loading
      emit("update:loading", {
        status: props.status,
        fetchingStatus: "loaded",
        count: totalTasks.value,
      });
    })
    .catch((err) => {
      console.log("error occurred", err);

      // Assign the total tasks to totalTasks.
      totalTasks.value = 0;

      // Emit update:loading
      emit("update:loading", {
        status: props.status,
        fetchingStatus: "error",
        count: totalTasks.value,
      });
    })
    .finally(() => {
      // Update the isInitiallyLoading
      isInitiallyLoading.value = false;

      // Update the isLoading
      isLoading.value = false;
    });
}

// Lifecycle Hooks
onMounted(() => {
  getTasks();
});
</script>
